import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { TabItem } from 'interfaces/shared.interface';
import { cn } from 'utils/global';

interface Props<T> {
  tabs: TabItem[];
  componentProps?: T;
  onActiveTabChange?: (index: number, id?: string) => void;
  dynamicTabIndex?: number;
  hideOnSingleTab?: boolean;
}

const Tabs = <T,>({ tabs, componentProps, onActiveTabChange, dynamicTabIndex, hideOnSingleTab }: Props<T>) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const { t } = useTranslation();

  const handleSelectTab = (tabIndex: number, id?: string) => {
    onActiveTabChange?.(tabIndex, id);
    setActiveTabIndex(tabIndex);
  };

  const TabContentComponent = tabs[activeTabIndex]?.component;

  useEffect(() => {
    if (dynamicTabIndex !== undefined && activeTabIndex !== dynamicTabIndex) {
      setActiveTabIndex(dynamicTabIndex);
    }
  }, [dynamicTabIndex]);

  return (
    <div>
      <div className="w-full mb-5 flex relative overflow-x-auto after:content-[''] after:absolute after:w-full after:h-[1px] after:bg-neutral-200 dark:after:bg-neutral-800 after:-bottom-0 after:left-0">
        <div className="flex-grow flex">
          {!(hideOnSingleTab && tabs.length === 1) &&
            tabs.map(({ id, title, dataCy }, index) => (
              <div
                className={cn(
                  'px-3 py-4 h-full rounded-t-[3px] leading-[0.875rem] cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-900 text-bodyMd-semibold relative z-10 text-neutral-500 border-b-2 border-neutral-200 dark:border-neutral-800',
                  {
                    'border-b-2 !border-primary-500 text-primary-500 bg-primary-100 hover:bg-primary-100 dark:hover:bg-primary-100':
                      index === activeTabIndex,
                  }
                )}
                key={id}
                onClick={() => handleSelectTab(index, id)}
                data-cy={dataCy}
              >
                {t(title)}
              </div>
            ))}
        </div>
        <div className="transition ease-linear duration-150 absolute bottom-0 h-0.5 bg-darkblue left-0" />
      </div>

      {TabContentComponent && typeof TabContentComponent === 'function' ? (
        <TabContentComponent {...componentProps} />
      ) : (
        TabContentComponent
      )}
    </div>
  );
};

export default Tabs;
